import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../../services/api';
import AttachmentIcon from '../Attachments/AttachmentIcon';
import styles from './History.module.css';

export default function HistoryPage({ user }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [requests, setRequests] = useState([]);
  const [search, setSearch] = useState(searchParams.get('q') || '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  const [dateFrom, setDateFrom] = useState(searchParams.get('from') || '');
  const [dateTo, setDateTo] = useState(searchParams.get('to') || '');
  const [dateMode, setDateMode] = useState(searchParams.get('dm') || '30d'); // '30d' | 'all' | '2024'.. | 'custom'
  const [loading, setLoading] = useState(true);
  const [actionRequestId, setActionRequestId] = useState(null);
  const [actionType, setActionType] = useState(null); // 'reject' | 'reopen'
  const [actionReason, setActionReason] = useState('');

  // Sync state to URL
  useEffect(() => {
    const params = {};
    if (search) params.q = search;
    if (statusFilter) params.status = statusFilter;
    if (dateMode !== '30d') params.dm = dateMode;
    if (dateMode === 'custom') {
      if (dateFrom) params.from = dateFrom;
      if (dateTo) params.to = dateTo;
    }
    setSearchParams(params, { replace: true });
  }, [search, statusFilter, dateMode, dateFrom, dateTo, setSearchParams]);

  useEffect(() => { load(); }, [statusFilter, dateMode]); // eslint-disable-line

  const getDateRange = () => {
    if (dateMode === 'all') return { dateFrom: '', dateTo: '' };
    if (dateMode === '30d') {
      const from = new Date();
      from.setDate(from.getDate() - 30);
      return { dateFrom: from.toISOString().split('T')[0], dateTo: '' };
    }
    if (dateMode === 'custom') return { dateFrom, dateTo };
    return { dateFrom: `${dateMode}-01-01`, dateTo: `${dateMode}-12-31` };
  };

  const load = async () => {
    setLoading(true);
    try {
      const { dateFrom: df, dateTo: dt } = getDateRange();
      const data = await api.getHistory(user.credential, { search, status: statusFilter, dateFrom: df, dateTo: dt });
      setRequests(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    load();
  };

  const handleReject = async () => {
    if (!actionReason.trim()) { alert('נא להזין סיבה'); return; }
    try {
      await api.rejectRequest(user.credential, actionRequestId, actionReason);
      setActionRequestId(null); setActionType(null); setActionReason('');
      load();
    } catch (e) { alert('שגיאה'); }
  };

  const handleReopen = async () => {
    if (!window.confirm('האם אתה בטוח שברצונך לפתוח מחדש את הבקשה?')) return;
    try {
      await api.reopenRequest(user.credential, actionRequestId, actionReason || null);
      setActionRequestId(null); setActionType(null); setActionReason('');
      load();
    } catch (e) { alert('שגיאה'); }
  };

  const formatDate = (iso) => new Date(iso).toLocaleDateString('he-IL');
  const formatAmount = (n) => `₪${n.toLocaleString('he-IL', { minimumFractionDigits: 0 })}`;

  return (
    <div className={styles.container}>
      <h2>היסטוריה</h2>

      {/* Stats cards */}
      {!loading && requests.length > 0 && (
        <div className={styles.statsGrid}>
          <div className={styles.statCard}>
            <div className={styles.statValue}>{requests.length}</div>
            <div className={styles.statLabel}>בקשות</div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statValue}>{formatAmount(requests.filter(r => r.status === 'Executed').reduce((s, r) => s + r.amount, 0))}</div>
            <div className={styles.statLabel}>בוצע</div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statValue}>{formatAmount(requests.filter(r => r.status === 'Open').reduce((s, r) => s + r.amount, 0))}</div>
            <div className={styles.statLabel}>פתוח</div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statValue}>{formatAmount(requests.reduce((s, r) => s + r.amount, 0))}</div>
            <div className={styles.statLabel}>סה"כ</div>
          </div>
        </div>
      )}

      {/* Search + Filters */}
      <form onSubmit={handleSearch} className={styles.filters}>
        <input
          className={styles.searchInput}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="חיפוש לפי שם, תיאור, הערה..."
        />
        <button type="submit" className={styles.searchBtn}>חפש</button>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={styles.filterSelect}>
          <option value="">כל הסטטוסים</option>
          <option value="Open">פתוח</option>
          <option value="Executed">בוצע</option>
          <option value="Rejected">נדחה</option>
        </select>
      </form>

      {/* Date filter */}
      <div className={styles.dateFilters}>
        <button className={`${styles.dateBtn} ${dateMode === '30d' ? styles.dateBtnActive : ''}`} onClick={() => { setDateMode('30d'); }}>30 יום</button>
        <button className={`${styles.dateBtn} ${dateMode === 'all' ? styles.dateBtnActive : ''}`} onClick={() => { setDateMode('all'); }}>הכל</button>
        <button className={`${styles.dateBtn} ${dateMode === '2026' ? styles.dateBtnActive : ''}`} onClick={() => { setDateMode('2026'); }}>2026</button>
        <button className={`${styles.dateBtn} ${dateMode === '2025' ? styles.dateBtnActive : ''}`} onClick={() => { setDateMode('2025'); }}>2025</button>
        <button className={`${styles.dateBtn} ${dateMode === '2024' ? styles.dateBtnActive : ''}`} onClick={() => { setDateMode('2024'); }}>2024</button>
        <button className={`${styles.dateBtn} ${dateMode === 'custom' ? styles.dateBtnActive : ''}`} onClick={() => { setDateMode('custom'); }}>מתקדם</button>
      </div>

      {dateMode === 'custom' && (
        <div className={styles.dateRange}>
          <label>מתאריך <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className={styles.dateInput} /></label>
          <label>עד תאריך <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className={styles.dateInput} /></label>
          <button className={styles.searchBtn} onClick={load}>סנן</button>
        </div>
      )}

      {/* Results count */}
      {!loading && <p className={styles.resultCount}>{requests.length} תוצאות</p>}

      {loading && <p className={styles.empty}>טוען...</p>}

      {!loading && requests.length === 0 && <p className={styles.empty}>לא נמצאו תוצאות.</p>}

      {/* Table */}
      {requests.length > 0 && (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>תאריך</th>
              <th>מגיש</th>
              <th>תיאור</th>
              <th>שויך ל</th>
              <th>חשבון</th>
              <th>סכום</th>
              <th>סטטוס</th>
              <th>פעולות</th>
            </tr>
          </thead>
          <tbody>
            {requests.map((r) => (
              <tr key={r.id}>
                <td>{formatDate(r.syncedAt)}</td>
                <td>
                  <div>{r.requesterName}</div>
                  <div className={styles.email}>{r.email}</div>
                </td>
                <td>
                  <div>{r.description}</div>
                  {r.note && <div className={styles.note}>{r.note}</div>}
                </td>
                <td>{r.applierName ? <span className={styles.applier}>{r.applierName}</span> : <span className={styles.noAccount}>—</span>}</td>
                <td>{r.bankDisplay || <span className={styles.noAccount}>לא שויך</span>}</td>
                <td className={styles.amount}>{formatAmount(r.amount)}</td>
                <td>
                  <span className={r.status === 'Open' ? styles.statusOpen : r.status === 'Rejected' ? styles.statusRejected : r.status === 'Approved' ? styles.statusOpen : styles.statusExecuted}>
                    {r.status === 'Open' ? 'פתוח' : r.status === 'Rejected' ? 'נדחה' : r.status === 'Approved' ? 'מאושר' : 'בוצע'}
                  </span>
                  <AttachmentIcon paymentRequestId={r.id} hasAttachments={r.hasAttachments} user={user} />
                </td>
                <td>
                  {actionRequestId === r.id ? (
                    <div className={styles.actionForm}>
                      <input
                        value={actionReason}
                        onChange={(e) => setActionReason(e.target.value)}
                        placeholder={actionType === 'reject' ? 'סיבת דחייה (חובה)' : 'סיבה (רשות)'}
                        className={styles.actionInput}
                      />
                      <button className={styles.actionConfirm} onClick={actionType === 'reject' ? handleReject : handleReopen}>✓</button>
                      <button className={styles.actionCancel} onClick={() => { setActionRequestId(null); setActionReason(''); }}>✕</button>
                    </div>
                  ) : (
                    <div className={styles.actionBtns}>
                      {r.status === 'Open' && (
                        <button className={styles.rejectBtn} onClick={() => { setActionRequestId(r.id); setActionType('reject'); }}>דחה</button>
                      )}
                      {(r.status === 'Executed' || r.status === 'Rejected') && (
                        <button className={styles.reopenBtn} onClick={() => { setActionRequestId(r.id); setActionType('reopen'); }}>פתח מחדש</button>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
